import React, { useState } from "react";
import {
  Table,
  Button,
  Tabs,
  Card,
  Modal,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Tag,
  Space,
  Descriptions,
  Divider,
  message,
  Popconfirm,
  Radio,
  Tooltip,
  Spin,
} from "antd";
import {
  DollarOutlined,
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  PayCircleOutlined,
  EditOutlined,
  CalendarOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useEmployeesQuery,
  useSalaryDetailsQuery,
  useUpdateSalaryDetailsMutation,
  useAdvancesQuery,
  useCreateAdvanceMutation,
  useDeleteAdvanceMutation,
  usePayrollsQuery,
  useCalculatePayrollPreviewMutation,
  useSavePayrollMutation,
  usePayPayrollMutation,
  useDeletePayrollMutation,
  useSectionAccess,
} from "../../Services";
import { PageHeader, tablePagination } from "../../components";
import { useAppBranding } from "../../Hook";
import { HRAction } from "../../Types";

const { Option } = Select;
const { TabPane } = Tabs;

export const PayrollManagementPage = () => {
  const [activeTab, setActiveTab] = useState("payrolls");
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [payrollSearch, setPayrollSearch] = useState("");

  const { appName, address, phone } = useAppBranding();

  const monthStr = selectedMonth.format("YYYY-MM");

  // Fetch active employees for selects/configs
  const { data: employeesRes, refetch: refetchEmployees } = useEmployeesQuery({
    page: 1,
    limit: 200,
  });
  const employees = employeesRes?.data || [];
  const activeEmployees = employees.filter((e: any) => e.status === "Active");

  // React Query Hooks for tab data
  const {
    data: payrolls = [],
    isLoading: payrollsLoading,
    refetch: refetchPayrolls,
  } = usePayrollsQuery({
    month: monthStr,
    search: payrollSearch,
  });

  const {
    data: advances = [],
    isLoading: advancesLoading,
    refetch: refetchAdvances,
  } = useAdvancesQuery();

  // Mutations
  const updateSalaryMutation = useUpdateSalaryDetailsMutation();
  const createAdvanceMutation = useCreateAdvanceMutation();
  const deleteAdvanceMutation = useDeleteAdvanceMutation();
  const calculatePreviewMutation = useCalculatePayrollPreviewMutation();
  const savePayrollMutation = useSavePayrollMutation();
  const payPayrollMutation = usePayPayrollMutation();
  const deletePayrollMutation = useDeletePayrollMutation();

  const { isAllowed } = useSectionAccess("hr");

  // --- Tab 1: Salary Config Form State ---
  const [salaryConfigEmployeeId, setSalaryConfigEmployeeId] = useState<
    string | null
  >(null);
  const [salaryConfigDrawerOpen, setSalaryConfigDrawerOpen] = useState(false);
  const [salaryForm] = Form.useForm();
  const salaryTypeVal = Form.useWatch("salary_type", salaryForm);

  const { data: salaryConfig, isLoading: salaryConfigLoading } =
    useSalaryDetailsQuery(salaryConfigEmployeeId || "", {
      enabled: !!salaryConfigEmployeeId && salaryConfigDrawerOpen,
    });

  React.useEffect(() => {
    if (salaryConfig) {
      salaryForm.setFieldsValue({
        salary_type: salaryConfig.salary_type,
        basic_salary: salaryConfig.basic_salary,
        day_rate: salaryConfig.day_rate,
        allowances: salaryConfig.allowances,
        deductions: salaryConfig.deductions,
        epf_rate: salaryConfig.epf_rate,
        epf_employer_rate: salaryConfig.epf_employer_rate,
        etf_rate: salaryConfig.etf_rate,
        ot_rate_hour: salaryConfig.ot_rate_hour,
        payment_method: salaryConfig.payment_method,
        bank_name: salaryConfig.bank_name || "",
        account_no: salaryConfig.account_no || "",
      });
    }
  }, [salaryConfig, salaryForm]);

  const handleOpenSalaryConfig = (employeeId: string) => {
    setSalaryConfigEmployeeId(employeeId);
    setSalaryConfigDrawerOpen(true);
  };

  const handleSaveSalaryConfig = async () => {
    try {
      const values = await salaryForm.validateFields();
      const res = await updateSalaryMutation.mutateAsync({
        employee_id: salaryConfigEmployeeId,
        ...values,
      });

      if (res?.success) {
        message.success("Salary configurations updated successfully!");
        setSalaryConfigDrawerOpen(false);
        setSalaryConfigEmployeeId(null);
        salaryForm.resetFields();
      } else {
        message.error(
          res?.message || "Failed to update salary configurations.",
        );
      }
    } catch (err) {
      message.error("Please fill all required fields correctly.");
    }
  };

  // --- Tab 2: Salary Advance State ---
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [advanceForm] = Form.useForm();

  const handleCreateAdvance = async () => {
    try {
      const values = await advanceForm.validateFields();
      const res = await createAdvanceMutation.mutateAsync({
        employee_id: values.employee_id,
        amount: values.amount,
        advance_date: values.advance_date.format("YYYY-MM-DD"),
        notes: values.notes,
      });

      if (res?.success) {
        message.success("Salary advance logged successfully.");
        setAdvanceModalOpen(false);
        advanceForm.resetFields();
        refetchAdvances();
      } else {
        message.error(res?.message || "Failed to save advance.");
      }
    } catch (err) {
      message.error("Please complete the form fields.");
    }
  };

  const handleDeleteAdvance = async (id: string) => {
    const res = await deleteAdvanceMutation.mutateAsync(id);
    if (res?.success) {
      message.success("Advance deleted successfully.");
      refetchAdvances();
    } else {
      message.error(res?.message || "Failed to delete advance.");
    }
  };

  // --- Tab 3: Monthly Payroll Generation ---
  const [payrollModalOpen, setPayrollModalOpen] = useState(false);
  const [genForm] = Form.useForm();
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [genStep, setGenStep] = useState<"select" | "preview">("select");

  const handleGeneratePreview = async () => {
    try {
      const values = await genForm.validateFields();
      const res = await calculatePreviewMutation.mutateAsync({
        employee_id: values.employee_id,
        payroll_month: monthStr,
        ot_hours: values.ot_hours || 0,
      });

      if (res?.success && res.data) {
        setPreviewData(res.data);
        setGenStep("preview");
      } else {
        message.error(res?.message || "Failed to generate payroll preview.");
      }
    } catch (err: any) {
      message.error(err?.message || "Please resolve form validation errors.");
    }
  };

  const handleSavePayroll = async (status: "Draft" | "Approved") => {
    if (!previewData) return;

    const payload = {
      ...previewData,
      status,
    };

    const res = await savePayrollMutation.mutateAsync(payload);
    if (res?.success) {
      message.success(`Payroll generated for employee as ${status}.`);
      setPayrollModalOpen(false);
      setPreviewData(null);
      setGenStep("select");
      genForm.resetFields();
      refetchPayrolls();
    } else {
      message.error(res?.message || "Failed to save payroll registry.");
    }
  };

  // --- Payroll Actions (Approve, Pay, Delete, Print) ---
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payingPayrollId, setPayingPayrollId] = useState<string | null>(null);
  const [paymentForm] = Form.useForm();

  const handleApprovePayroll = async (record: any) => {
    const payload = {
      ...record,
      status: "Approved",
    };
    const res = await savePayrollMutation.mutateAsync(payload);
    if (res?.success) {
      message.success("Payroll approved successfully.");
      refetchPayrolls();
    } else {
      message.error(res?.message || "Failed to approve payroll.");
    }
  };

  const handleOpenPayment = (id: string) => {
    setPayingPayrollId(id);
    setPaymentModalOpen(true);
    paymentForm.setFieldsValue({ payment_method: "Cash" });
  };

  const handleConfirmPayment = async () => {
    if (!payingPayrollId) return;
    try {
      const values = await paymentForm.validateFields();
      const res = await payPayrollMutation.mutateAsync({
        id: payingPayrollId,
        payment_method: values.payment_method,
        payment_note: values.payment_note,
      });

      if (res?.success) {
        message.success("Payroll paid successfully.");
        setPaymentModalOpen(false);
        setPayingPayrollId(null);
        paymentForm.resetFields();
        refetchPayrolls();
      } else {
        message.error(res?.message || "Failed to process payment status.");
      }
    } catch (err) {
      message.error("Please complete the payment details.");
    }
  };

  const handleDeletePayroll = async (id: string) => {
    const res = await deletePayrollMutation.mutateAsync(id);
    if (res?.success) {
      message.success("Payroll record deleted successfully.");
      refetchPayrolls();
    } else {
      message.error(res?.message || "Failed to delete payroll record.");
    }
  };

  // --- Print Payslip Modal ---
  const [payslipModalOpen, setPayslipModalOpen] = useState(false);
  const [activePayslip, setActivePayslip] = useState<any | null>(null);

  const handleOpenPayslip = (record: any) => {
    setActivePayslip(record);
    setPayslipModalOpen(true);
  };

  const handlePrintPayslip = () => {
    const printContent = document.getElementById("payslip-print-area");
    if (!printContent) return;

    const winPrint = window.open(
      "",
      "",
      "left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0",
    );
    if (winPrint) {
      winPrint.document.write(`
        <html>
          <head>
            <title>Payslip - ${activePayslip?.first_name} ${activePayslip?.last_name}</title>
            <style>
              body { font-family: 'Inter', Arial, sans-serif; padding: 40px; color: #333; line-height: 1.5; }
              .header { text-align: center; margin-bottom: 30px; }
              .header h2 { margin: 5px 0; text-transform: uppercase; font-size: 20px; }
              .header p { margin: 2px 0; color: #666; font-size: 13px; }
              .info-table, .calc-table { width: 100%; border-collapse: collapse; margin-bottom: 25px; }
              .info-table td { padding: 6px; font-size: 13px; vertical-align: top; }
              .info-table td.label { font-weight: bold; width: 25%; color: #555; }
              .calc-table th, .calc-table td { border: 1px solid #ddd; padding: 10px; font-size: 13px; text-align: left; }
              .calc-table th { background-color: #f9f9f9; font-weight: bold; }
              .calc-table td.amount { text-align: right; }
              .calc-table tr.total-row { font-weight: bold; background-color: #f5f5f5; }
              .footer-signature { display: flex; justify-content: space-between; margin-top: 80px; }
              .sig-line { border-top: 1px solid #999; width: 200px; text-align: center; padding-top: 5px; font-size: 12px; color: #666; }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              }
            </script>
          </body>
        </html>
      `);
      winPrint.document.close();
    }
  };

  // --- Table Column Definitions ---

  // Tab 1 Column: Salary Configuration
  const salaryConfigColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_: any, rec: any) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm m-0 leading-tight">
            {rec.first_name} {rec.last_name}
          </p>
          <span className="text-xs text-gray-400 font-medium">
            {rec.designation || "Staff"}
          </span>
        </div>
      ),
    },
    {
      title: "NIC",
      dataIndex: "nic",
      key: "nic",
      render: (v: string) => v || <span className="text-gray-300">—</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={s === "Active" ? "success" : "default"}>{s}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_: any, rec: any) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleOpenSalaryConfig(rec.id)}
          className="text-blue-600 hover:text-blue-800"
        >
          Configure
        </Button>
      ),
    },
  ];

  // Tab 2 Column: Salary Advances
  const advancesColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_: any, rec: any) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm m-0 leading-tight">
            {rec.first_name} {rec.last_name}
          </p>
          <span className="text-xs text-gray-400 font-medium">
            {rec.nic || "No NIC"}
          </span>
        </div>
      ),
    },
    {
      title: "Advance Date",
      dataIndex: "advance_date",
      key: "advance_date",
      render: (v: string) => dayjs(v).format("YYYY-MM-DD"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-semibold text-gray-800">Rs. {v.toFixed(2)}</span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, rec: any) =>
        rec.is_deducted === 1 ? (
          <Tag color="success">Deducted in Payroll</Tag>
        ) : (
          <Tag color="warning">Pending Deduction</Tag>
        ),
    },
    {
      title: "Remarks",
      dataIndex: "notes",
      key: "notes",
      render: (v: string) => v || <span className="text-gray-300">—</span>,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_: any, rec: any) =>
        rec.is_deducted === 0 ? (
          <Popconfirm
            title="Delete this salary advance log?"
            onConfirm={() => handleDeleteAdvance(rec.id)}
            okText="Delete"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        ) : (
          <Tooltip title="Deducted advances cannot be deleted">
            <Button
              type="text"
              disabled
              icon={<DeleteOutlined />}
              size="small"
            />
          </Tooltip>
        ),
    },
  ];

  // Tab 3 Column: Monthly Payroll Registers
  const payrollsColumns = [
    {
      title: "Employee",
      key: "name",
      render: (_: any, rec: any) => (
        <div>
          <p className="font-semibold text-gray-800 text-sm m-0 leading-tight">
            {rec.first_name} {rec.last_name}
          </p>
          <span className="text-xs text-gray-400 font-medium">
            {rec.designation || "Staff"} ({rec.salary_type})
          </span>
        </div>
      ),
    },
    {
      title: "Month",
      dataIndex: "payroll_month",
      key: "payroll_month",
    },
    {
      title: "Attendance Stats",
      key: "attendance",
      render: (_: any, rec: any) => (
        <span className="text-xs text-gray-600">
          Present: <strong>{rec.present_days}</strong>, Absent:{" "}
          <strong>{rec.absent_days}</strong>
        </span>
      ),
    },
    {
      title: "Gross Salary",
      dataIndex: "gross_salary",
      key: "gross_salary",
      align: "right" as const,
      render: (v: number) => `Rs. ${parseFloat(v as any).toFixed(2)}`,
    },
    {
      title: "Total Deductions",
      dataIndex: "total_deductions",
      key: "total_deductions",
      align: "right" as const,
      render: (v: number) => `Rs. ${parseFloat(v as any).toFixed(2)}`,
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      align: "right" as const,
      render: (v: number) => (
        <span className="font-bold text-emerald-600">
          Rs. {parseFloat(v as any).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => {
        let color = "default";
        if (s === "Approved") color = "blue";
        if (s === "Paid") color = "success";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_: any, rec: any) => (
        <Space size="middle">
          {rec.status === "Draft" && (
            <Tooltip title="Approve Draft">
              <Button
                type="text"
                className="text-blue-500"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprovePayroll(rec)}
              />
            </Tooltip>
          )}
          {rec.status === "Approved" && (
            <Tooltip title="Mark Paid">
              <Button
                type="text"
                className="text-emerald-500"
                icon={<PayCircleOutlined />}
                onClick={() => handleOpenPayment(rec.id)}
              />
            </Tooltip>
          )}
          <Tooltip title="View Payslip">
            <Button
              type="text"
              icon={<PrinterOutlined />}
              onClick={() => handleOpenPayslip(rec)}
            />
          </Tooltip>
          {rec.status !== "Paid" && (
            <Popconfirm
              title="Delete this payroll registry?"
              onConfirm={() => handleDeletePayroll(rec.id)}
              okText="Delete"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50/50">
      <PageHeader
        title="Payroll & HR Manager"
        subText="Manage payroll registers, salary details, and salary advances"
        handleButtons={[
          ...(isAllowed(HRAction.ManagePayroll)
            ? [
                {
                  title: "Add Salary Advance",
                  icon: PlusOutlined,
                  onClick: () => {
                    advanceForm.resetFields();
                    setAdvanceModalOpen(true);
                  },
                },
              ]
            : []),
          ...(isAllowed(HRAction.ManagePayroll)
            ? [
                {
                  title: "Generate Payroll",
                  icon: DollarOutlined,
                  onClick: () => {
                    genForm.resetFields();
                    setPreviewData(null);
                    setGenStep("select");
                    setPayrollModalOpen(true);
                  },
                },
              ]
            : []),
        ]}
        searchBar={
          activeTab === "payrolls" ? (
            <div className="flex gap-3">
              <DatePicker
                value={selectedMonth}
                onChange={(m) => m && setSelectedMonth(m)}
                picker="month"
                format="YYYY-MM"
                allowClear={false}
                className="w-40 h-9"
              />
              <Input
                placeholder="Search payrolls..."
                prefix={<SearchOutlined />}
                value={payrollSearch}
                onChange={(e) => setPayrollSearch(e.target.value)}
                allowClear
                className="w-60 h-9"
              />
            </div>
          ) : undefined
        }
      />

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          if (key === "payrolls") refetchPayrolls();
          if (key === "advances") refetchAdvances();
          if (key === "configs") refetchEmployees();
        }}
        className="mt-4 bg-white p-6 rounded-lg border border-gray-200 shadow-sm"
      >
        <TabPane tab="Monthly Payrolls" key="payrolls">
          <Table
            dataSource={payrolls}
            columns={payrollsColumns}
            rowKey="id"
            loading={payrollsLoading}
            scroll={{ x: 1000 }}
            locale={{
              emptyText: `No payroll records generated for ${monthStr}.`,
            }}
          />
        </TabPane>

        <TabPane tab="Salary Advances" key="advances">
          <Table
            dataSource={advances}
            columns={advancesColumns}
            rowKey="id"
            loading={advancesLoading}
            scroll={{ x: 800 }}
            locale={{ emptyText: "No salary advance logs found." }}
          />
        </TabPane>

        <TabPane tab="Salary Configurations" key="configs">
          <Table
            dataSource={activeEmployees}
            columns={salaryConfigColumns}
            rowKey="id"
            scroll={{ x: 800 }}
          />
        </TabPane>
      </Tabs>

      {/* --- Salary Config Drawer --- */}
      <Drawer
        title="Configure Salary Settings"
        width={500}
        open={salaryConfigDrawerOpen}
        onClose={() => setSalaryConfigDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <Button onClick={() => setSalaryConfigDrawerOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveSalaryConfig}
              loading={updateSalaryMutation.isPending}
            >
              Save Configuration
            </Button>
          </div>
        }
      >
        <Spin spinning={salaryConfigLoading || updateSalaryMutation.isPending}>
          <Form form={salaryForm} layout="vertical">
            <Form.Item
              label="Salary Structure Type"
              name="salary_type"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="Monthly">Monthly Fixed Salary</Option>
                <Option value="Daily">Daily Wage Rate</Option>
              </Select>
            </Form.Item>

            {salaryTypeVal === "Daily" ? (
              <Form.Item
                label="Daily Rate (LKR)"
                name="day_rate"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber className="w-full" min={0} precision={2} />
              </Form.Item>
            ) : (
              <Form.Item
                label="Monthly Basic Salary (LKR)"
                name="basic_salary"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber className="w-full" min={0} precision={2} />
              </Form.Item>
            )}

            <Form.Item label="Standard Allowances (LKR)" name="allowances">
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>

            <Form.Item label="Standard Deductions (LKR)" name="deductions">
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>

            <div className="grid grid-cols-3 gap-2">
              <Form.Item label="EPF Rate %" name="epf_rate">
                <InputNumber
                  className="w-full"
                  min={0}
                  max={100}
                  precision={2}
                />
              </Form.Item>
              <Form.Item label="Employer EPF %" name="epf_employer_rate">
                <InputNumber
                  className="w-full"
                  min={0}
                  max={100}
                  precision={2}
                />
              </Form.Item>
              <Form.Item label="Employer ETF %" name="etf_rate">
                <InputNumber
                  className="w-full"
                  min={0}
                  max={100}
                  precision={2}
                />
              </Form.Item>
            </div>

            <Form.Item
              label="Hourly Overtime (OT) Rate (LKR)"
              name="ot_rate_hour"
            >
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>

            <Form.Item label="Payment Method" name="payment_method">
              <Select>
                <Option value="Cash">Cash</Option>
                <Option value="Bank Transfer">Bank Transfer</Option>
                <Option value="Cheque">Cheque</Option>
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) =>
                prev.payment_method !== curr.payment_method
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("payment_method") === "Bank Transfer" ? (
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <Form.Item
                      label="Bank Name"
                      name="bank_name"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input placeholder="e.g. Commercial Bank" />
                    </Form.Item>
                    <Form.Item
                      label="Account Number"
                      name="account_no"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Input placeholder="e.g. 1234567890" />
                    </Form.Item>
                  </div>
                ) : null
              }
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>

      {/* --- Salary Advance Modal --- */}
      <Modal
        title="Log Employee Salary Advance"
        open={advanceModalOpen}
        onCancel={() => setAdvanceModalOpen(false)}
        onOk={handleCreateAdvance}
        confirmLoading={createAdvanceMutation.isPending}
      >
        <Form form={advanceForm} layout="vertical" className="pt-2">
          <Form.Item
            label="Employee"
            name="employee_id"
            rules={[{ required: true, message: "Please select an employee" }]}
          >
            <Select placeholder="Select active employee">
              {activeEmployees.map((e: any) => (
                <Option key={e.id} value={e.id}>
                  {e.first_name} {e.last_name} ({e.designation || "Staff"})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Advance Amount (LKR)"
            name="amount"
            rules={[{ required: true, message: "Please enter amount" }]}
          >
            <InputNumber className="w-full" min={1} precision={2} />
          </Form.Item>

          <Form.Item
            label="Date Given"
            name="advance_date"
            rules={[{ required: true, message: "Select date" }]}
          >
            <DatePicker
              className="w-full"
              format="YYYY-MM-DD"
              defaultValue={dayjs()}
            />
          </Form.Item>

          <Form.Item label="Remarks / Notes" name="notes">
            <Input.TextArea placeholder="Enter description..." rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* --- Generate Payroll Drawer --- */}
      <Drawer
        title={`Generate Payroll for Month: ${monthStr}`}
        width={600}
        open={payrollModalOpen}
        onClose={() => setPayrollModalOpen(false)}
        footer={
          genStep === "preview" ? (
            <div className="flex justify-end gap-3">
              <Button onClick={() => setGenStep("select")}>Back</Button>
              <Button
                onClick={() => handleSavePayroll("Draft")}
                loading={savePayrollMutation.isPending}
              >
                Save as Draft
              </Button>
              <Button
                type="primary"
                onClick={() => handleSavePayroll("Approved")}
                loading={savePayrollMutation.isPending}
              >
                Approve & Finalize
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                type="primary"
                onClick={handleGeneratePreview}
                loading={calculatePreviewMutation.isPending}
              >
                Preview Summary
              </Button>
            </div>
          )
        }
      >
        {genStep === "select" ? (
          <Form form={genForm} layout="vertical" className="pt-2">
            <Form.Item
              label="Select Employee"
              name="employee_id"
              rules={[{ required: true, message: "Select employee" }]}
            >
              <Select placeholder="Select active employee">
                {activeEmployees.map((e: any) => (
                  <Option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name} ({e.designation || "Staff"})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Overtime (OT) Hours for Month" name="ot_hours">
              <InputNumber
                className="w-full"
                min={0}
                defaultValue={0}
                precision={2}
              />
            </Form.Item>
          </Form>
        ) : (
          <Spin spinning={calculatePreviewMutation.isPending}>
            {previewData && (
              <div className="space-y-4">
                <Descriptions
                  title="Salary Preview Details"
                  bordered
                  column={1}
                  size="small"
                >
                  <Descriptions.Item label="Payroll Month">
                    <strong>{previewData.payroll_month}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Salary Structure Type">
                    {previewData.salary_type}
                  </Descriptions.Item>
                  <Descriptions.Item label="Days Present (Weighted)">
                    {previewData.present_days}
                  </Descriptions.Item>
                  <Descriptions.Item label="Days Absent (Weighted)">
                    {previewData.absent_days}
                  </Descriptions.Item>
                  <Descriptions.Item label="Calculated Basic Salary">
                    Rs. {previewData.basic_salary.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Monthly Allowances">
                    Rs. {previewData.allowances.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Overtime (OT) Amount">
                    Rs. {previewData.ot_amount.toFixed(2)} (
                    {previewData.ot_hours} hrs)
                  </Descriptions.Item>
                  <Descriptions.Item label="Gross Salary">
                    <strong>Rs. {previewData.gross_salary.toFixed(2)}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="EPF Employee (Deduction)">
                    Rs. {previewData.employee_epf.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Salary Advance Deductions">
                    <span className="text-red-500 font-semibold">
                      Rs. {previewData.salary_advance.toFixed(2)}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Standard Deductions">
                    Rs. {previewData.deductions.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Deductions Summary">
                    Rs. {previewData.total_deductions.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Net Payable Salary">
                    <span className="text-emerald-600 font-bold text-lg">
                      Rs. {previewData.net_salary.toFixed(2)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>

                <Divider style={{ margin: "12px 0" }} />

                <Descriptions
                  title="Employer Contributions (Company Cost)"
                  bordered
                  column={1}
                  size="small"
                >
                  <Descriptions.Item label="Employer EPF">
                    Rs. {previewData.employer_epf.toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Employer ETF">
                    Rs. {previewData.employer_etf.toFixed(2)}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </Spin>
        )}
      </Drawer>

      {/* --- Process Payment Modal --- */}
      <Modal
        title="Record Payroll Cash Outflow / Payment"
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        onOk={handleConfirmPayment}
        confirmLoading={payPayrollMutation.isPending}
      >
        <Form form={paymentForm} layout="vertical" className="pt-2">
          <Form.Item
            label="Payment Method"
            name="payment_method"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="Cash">Cash</Option>
              <Option value="Bank Transfer">Bank Transfer</Option>
              <Option value="Cheque">Cheque</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Payment Note / Transaction Ref" name="payment_note">
            <Input.TextArea
              placeholder="Enter cheque number, bank receipt ref..."
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* --- Payslip Modal --- */}
      <Modal
        title="Employee Payslip Voucher"
        open={payslipModalOpen}
        onCancel={() => setPayslipModalOpen(false)}
        width={650}
        footer={
          <div className="flex justify-end gap-3">
            <Button onClick={() => setPayslipModalOpen(false)}>Close</Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintPayslip}
            >
              Print Payslip
            </Button>
          </div>
        }
      >
        {activePayslip && (
          <div
            id="payslip-print-area"
            className="p-4 border border-gray-200 rounded bg-white"
          >
            <div
              className="header"
              style={{ textAlign: "center", marginBottom: "20px" }}
            >
              <h2
                style={{
                  margin: "2px 0",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                {appName.toUpperCase()}
              </h2>
              {address && (
                <p style={{ margin: "1px 0", fontSize: "12px" }}>{address}</p>
              )}
              {phone && (
                <p style={{ margin: "1px 0", fontSize: "12px" }}>
                  Tel: {phone}
                </p>
              )}
              <h3
                style={{
                  margin: "10px 0 0",
                  fontSize: "14px",
                  textDecoration: "underline",
                }}
              >
                SALARY PAYSLIP - {activePayslip.payroll_month}
              </h3>
            </div>

            <table
              className="info-table"
              style={{ width: "100%", marginBottom: "15px" }}
            >
              <tbody>
                <tr>
                  <td className="label">Employee Name:</td>
                  <td>
                    <strong>
                      {activePayslip.first_name} {activePayslip.last_name}
                    </strong>
                  </td>
                  <td className="label">Designation:</td>
                  <td>{activePayslip.designation || "Staff"}</td>
                </tr>
                <tr>
                  <td className="label">NIC:</td>
                  <td>{activePayslip.nic || "—"}</td>
                  <td className="label">Salary Structure:</td>
                  <td>{activePayslip.salary_type}</td>
                </tr>
                <tr>
                  <td className="label">Present Days:</td>
                  <td>{activePayslip.present_days}</td>
                  <td className="label">Absent Days:</td>
                  <td>{activePayslip.absent_days}</td>
                </tr>
                <tr>
                  <td className="label">Payment Status:</td>
                  <td>
                    <strong>{activePayslip.status}</strong>
                    {activePayslip.paid_at &&
                      ` (Paid at ${dayjs(activePayslip.paid_at).format("YYYY-MM-DD")})`}
                  </td>
                  <td className="label">Method:</td>
                  <td>{activePayslip.payment_method || "—"}</td>
                </tr>
              </tbody>
            </table>

            <table
              className="calc-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th>Salary Components</th>
                  <th style={{ textAlign: "right" }}>Earnings (LKR)</th>
                  <th style={{ textAlign: "right" }}>Deductions (LKR)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Basic Salary</td>
                  <td className="amount">
                    {parseFloat(activePayslip.basic_salary).toFixed(2)}
                  </td>
                  <td className="amount">—</td>
                </tr>
                {parseFloat(activePayslip.allowances) > 0 && (
                  <tr>
                    <td>Fixed Allowances</td>
                    <td className="amount">
                      {parseFloat(activePayslip.allowances).toFixed(2)}
                    </td>
                    <td className="amount">—</td>
                  </tr>
                )}
                {parseFloat(activePayslip.ot_amount) > 0 && (
                  <tr>
                    <td>Overtime (OT) ({activePayslip.ot_hours} hrs)</td>
                    <td className="amount">
                      {parseFloat(activePayslip.ot_amount).toFixed(2)}
                    </td>
                    <td className="amount">—</td>
                  </tr>
                )}
                {parseFloat(activePayslip.employee_epf) > 0 && (
                  <tr>
                    <td>EPF (Employee Contribution)</td>
                    <td className="amount">—</td>
                    <td className="amount">
                      {parseFloat(activePayslip.employee_epf).toFixed(2)}
                    </td>
                  </tr>
                )}
                {parseFloat(activePayslip.salary_advance) > 0 && (
                  <tr>
                    <td>Salary Advance Deductions</td>
                    <td className="amount">—</td>
                    <td className="amount" style={{ color: "#ef4444" }}>
                      {parseFloat(activePayslip.salary_advance).toFixed(2)}
                    </td>
                  </tr>
                )}
                {parseFloat(activePayslip.deductions) > 0 && (
                  <tr>
                    <td>Standard Fixed Deductions</td>
                    <td className="amount">—</td>
                    <td className="amount">
                      {parseFloat(activePayslip.deductions).toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr
                  className="total-row"
                  style={{ fontWeight: "bold", backgroundColor: "#f9f9f9" }}
                >
                  <td>Totals</td>
                  <td className="amount">
                    {parseFloat(activePayslip.gross_salary).toFixed(2)}
                  </td>
                  <td className="amount">
                    {parseFloat(activePayslip.total_deductions).toFixed(2)}
                  </td>
                </tr>
                <tr
                  className="total-row"
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <td>Net Payable Salary</td>
                  <td
                    colSpan={2}
                    className="amount"
                    style={{ color: "#059669", textAlign: "right" }}
                  >
                    Rs. {parseFloat(activePayslip.net_salary).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              className="footer-signature"
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "50px",
              }}
            >
              <div
                className="sig-line"
                style={{
                  borderTop: "1px solid #999",
                  width: "180px",
                  textAlign: "center",
                  paddingTop: "5px",
                  fontSize: "11px",
                }}
              >
                Employer Signature
              </div>
              <div
                className="sig-line"
                style={{
                  borderTop: "1px solid #999",
                  width: "180px",
                  textAlign: "center",
                  paddingTop: "5px",
                  fontSize: "11px",
                }}
              >
                Employee Signature
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
